import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {ClientService} from '@app/admin/client.service';

import {RequestService} from '@app/request-service.interface';
import {User} from '@app/shared/user.service';

import {combineLatest, forkJoin, from, Observable, ReplaySubject, Subscription} from 'rxjs';
import {filter, map, take, tap} from 'rxjs/operators';
import {AssetService} from '../asset';
import {deserialize, deserializeArray} from '../serializable';
import {StateService} from '../state/state.service';
import {Stream} from './models';

@Injectable({
    providedIn: 'root',
})
export class StreamService implements RequestService {
    public streams: ReplaySubject<Stream[]>;
    public stream: ReplaySubject<Stream>;

    private cache: Map<string, Stream>;
    private routeStreamId: string;
    private subscriptions: Subscription[];

    constructor(
        private stateService: StateService,
        private assetService: AssetService,
        private clientService: ClientService,
        private http: HttpClient,
        private router: Router,
    ) {
        this.streams = new ReplaySubject<Stream[]>(1);
        this.stream = new ReplaySubject<Stream>(1);
        this.cache = new Map<string, Stream>();
        this.routeStreamId = null;
        this.subscriptions = [];

        this.clientService.client.subscribe((client) => {
            this.initRequests();
        });
    }

    initRequests() {
        this.subscriptions.map((sub) => sub.unsubscribe());
        this.subscriptions = [];
        this.cache.clear();

        const requestObservable = this.listStreams().pipe(
            tap((streams) => {
                this.populateCache(streams);
                this.streams.next(streams);
            }),
        );
        const stateObservable = this.stateService.state.pipe(
            filter((state) => Boolean(state.streamId)),
        );

        // Subscribe here so we get all stream changes
        let sub = stateObservable.subscribe((state) => {
            this.setStream(this.cache.get(state.streamId));
        });
        this.subscriptions.push(sub);

        // FIXME: This might not be a good idea
        sub = combineLatest([requestObservable, stateObservable])
            .pipe(take(1))
            .subscribe((results) => {
                const streams = results[0];
                const state = results[1];

                this.populateCache(streams);
                this.setStream(this.cache.get(state.streamId));
            });
        this.subscriptions.push(sub);
    }

    listStreams() {
        return this.http
            .get<Stream[]>('/api/stream/')
            .pipe(map((streams) => deserializeArray(Stream, streams)));
    }

    setStream(stream: Stream) {
        this.stream.next(stream);
    }

    getStream(streamId: string): Stream {
        return this.cache.get(streamId);
    }

    resolve(streamId: string): Observable<Stream> {
        const promise = new Promise<Stream>((resolve, reject) => {
            const cachedStream = this.cache.get(streamId);
            if (cachedStream) {
                resolve(cachedStream);
            } else {
                const url = `/api/stream/${streamId}/`;
                this.http.get<Stream>(url).subscribe((stream) => {
                    this.cache.set(stream.id, stream);
                    resolve(stream);
                });
            }
        });

        return from(promise);
    }

    updateStream(id: string, data: any) {
        const url = `/api/stream/${id}/`;
        const options = data;
        options['withCredentials'] = true;

        this.http
            .patch<Stream>(url, options)
            .pipe(map((stream) => deserialize(Stream, stream)))
            .subscribe((stream) => {
                this.cache.set(stream.id, stream);
                const streams = Array.from(this.cache.values()).filter(
                    (stream) => stream.parent === null,
                );
                // Update the stream child objects with any changes from the cached objects
                streams.forEach((stream) => {
                    stream.children = stream.children.map((child) => this.cache.get(child.id));
                });
                this.streams.next(streams);
            });
    }

    createStream(name: string, parentId?: string) {
        const url = `/api/stream/`;
        const options = {
            name,
            parent: parentId,
            withCredentials: true,
        };

        this.http
            .post<Stream>(url, options)
            .pipe(map((stream) => deserialize(Stream, stream)))
            .subscribe((stream) => {
                this.cache.set(stream.id, stream);
                if (stream.parent) {
                    this.cache.get(stream.parent).children.push(stream);
                }
                const streams = Array.from(this.cache.values()).filter(
                    (stream) => stream.parent === null,
                );
                this.streams.next(streams);
            });
    }

    removeStream(streamId: string, merge: boolean) {
        const url = `/api/stream/${streamId}`;
        const options = {
            withCredentials: true,
            merge,
        };

        this.http.request('DELETE', url, { body: { merge: merge } }).subscribe(() => {
            this.cache.delete(streamId);
            const streams = Array.from(this.cache.values()).filter(
                (stream) => stream.parent === null,
            );
            this.streams.next(streams);
        });
    }

    removeAssets(streamId: string, assetIds: string[]) {
        const observables = assetIds.map((assetId) => {
            return this.http.delete(`/api/stream/${streamId}/asset/${assetId}/`);
        });

        forkJoin(observables).subscribe(() => {
            this.assetService.removeAssets(assetIds);
        });
    }

    assignAssets(streamId: string, assetIds: string[], from: string = null) {
        const url = `/api/stream/${streamId}/assign/`;
        const options = {
            asset_ids: assetIds,
            withCredentials: true,
        };
        if (from !== null) {
            options['from'] = from;
        }

        return this.http.put(url, options);
    }

    addMembers(streamId: string, members: User[]) {
        const url = `/api/stream/${streamId}/addMembers/`;
        const options = {
            user_ids: members.map((member) => member.id),
            withCredentials: true,
        };

        this.http
            .post<Stream>(url, options)
            .pipe(map((result) => deserialize(Stream, result)))
            .subscribe((stream) => this.updateCache(stream));
    }

    removeMember(streamId: string, member: User) {
        const url = `/api/stream/${streamId}/removeMember/`;
        const options = {
            user_id: member.id,
            withCredentials: true,
        };

        this.http
            .post<Stream>(url, options)
            .pipe(map((result) => deserialize(Stream, result)))
            .subscribe((stream) => this.updateCache(stream));
    }

    private populateCache(streams: Stream[]) {
        if (this.cache.size > 0) {
            return;
        }
        streams.forEach((stream) => {
            this.cache.set(stream.id, stream);
            stream.children.forEach((child) => this.cache.set(child.id, child));
        });
    }

    private updateCache(stream: Stream) {
        this.cache.set(stream.id, stream);
        if (stream.parent) {
            this.cache.get(stream.parent).children.push(stream);
        }
        const streams = Array.from(this.cache.values()).filter((stream) => stream.parent === null);
        this.streams.next(streams);
    }
}
